import React from 'react';
import { useThemeMode } from '../../design-system/theme';
import { Helmet } from 'react-helmet-async';
import SiteRouteHelmet from '../../components/SiteRouteHelmet';
import { ShieldCheck } from 'lucide-react';
import LoginRedirect from '../../components/LoginRedirect';
import './AuthFirstSalesPage.css';
import './ClarityAuth.css';

type AuthFirstSalesPageProps = {
  analyticsContext?: NonNullable<React.ComponentProps<typeof LoginRedirect>>['analyticsContext'];
  authDescriptionOverride?: string;
  authHeadingIdOverride?: string;
  authSourceOverride?: string;
  entryMode?: 'discovery' | 'checkout' | 'login' | 'signup';
  forceFreshLogin?: boolean;
  hideAuthHeaderFromAssistiveTech?: boolean;
  canonicalHrefOverride?: string;
  documentTitleOverride?: string;
  metaDescriptionOverride?: string;
  newSignupReturnTo?: string;
  onAuthenticated?: () => void;
  presentation?: 'page' | 'overlay' | 'embedded';
  returnTo: string;
};

const AuthFirstSalesPage = ({
  analyticsContext,
  authDescriptionOverride,
  authHeadingIdOverride,
  authSourceOverride,
  canonicalHrefOverride,
  documentTitleOverride,
  entryMode = 'discovery',
  forceFreshLogin = false,
  hideAuthHeaderFromAssistiveTech = false,
  metaDescriptionOverride,
  newSignupReturnTo,
  onAuthenticated,
  presentation = 'page',
  returnTo,
}: AuthFirstSalesPageProps): React.ReactElement => {
  const [websiteTheme] = useThemeMode();
  const isCheckoutEntry = entryMode === 'checkout';
  const isLoginEntry = entryMode === 'login';
  const isSignupEntry = entryMode === 'signup';
  const authHeading = isCheckoutEntry
    ? 'Create or sign in to continue'
    : isLoginEntry
      ? 'Sign in to BotSpot'
      : 'Create your account';
  const defaultAuthDescription = isCheckoutEntry
    ? "We'll sign you in, or create an account. Your plan is saved."
    : isLoginEntry
      ? "Sign in with Google, Apple, or email. Create an account if you're new."
      : "We'll create an account, or sign you in if one exists.";
  const authDescription = authDescriptionOverride || defaultAuthDescription;
  const authSource =
    authSourceOverride ||
    (isCheckoutEntry
      ? 'checkout_auth_first'
      : isLoginEntry
        ? 'login_auth_first'
        : isSignupEntry
          ? 'signup_auth_first'
          : 'sales_auth_first');
  const isOverlay = presentation === 'overlay';
  const isEmbedded = presentation === 'embedded';
  const Content = isEmbedded ? 'section' : 'main';

  const authContent = (
    <aside
      aria-label={
        isCheckoutEntry
          ? 'Create or sign in to continue'
          : isLoginEntry
            ? 'Sign in to BotSpot'
            : 'Create your BotSpot account'
      }
      className="auth-first-sales__conversion"
      id="auth-card-section"
    >
      <div className="auth-first-sales__auth-card">
        <LoginRedirect
          analyticsContext={analyticsContext}
          descriptionOverride={authDescription}
          forceFreshLoginOverride={forceFreshLogin}
          headingIdOverride={authHeadingIdOverride}
          headingOverride={authHeading}
          hideDescriptionOverride={false}
          hideHeaderFromAssistiveTechOverride={hideAuthHeaderFromAssistiveTech}
          newSignupReturnToOverride={newSignupReturnTo}
          onAuthenticated={onAuthenticated}
          productLedOverride={false}
          returnToOverride={returnTo}
          sourceOverride={authSource}
        />
      </div>

      {isCheckoutEntry && (
        <div className="auth-first-sales__assurance">
          <ShieldCheck aria-hidden="true" focusable="false" />
          <p>Nothing is charged on this page.</p>
        </div>
      )}

      {isCheckoutEntry ? (
        <div className="auth-first-sales__checkout-context">
          <a className="auth-first-sales__pricing-link" href="/pricing">
            Return to pricing
          </a>
        </div>
      ) : null}
    </aside>
  );

  return (
    <div
      className={`auth-first-sales auth-page bsui-scope auth-first-sales--clarity auth-first-sales--auth-only${isOverlay || isEmbedded ? ' auth-first-sales--overlay' : ''}`}
      data-bs-theme={websiteTheme}
      data-design="clarity"
      data-entry-mode={entryMode}
      data-presentation={presentation}
    >
      {isEmbedded ? null : entryMode === 'discovery' &&
        !documentTitleOverride &&
        !canonicalHrefOverride ? (
        <SiteRouteHelmet path="/sales" />
      ) : (
        <Helmet>
          <title>
            {documentTitleOverride ||
              (isCheckoutEntry
                ? 'Create Your BotSpot Account | Continue to Checkout'
                : isLoginEntry
                  ? 'Sign In to BotSpot'
                  : isSignupEntry
                    ? 'Create Your BotSpot Account'
                    : 'BotSpot | Build and Backtest a Trading Bot with AI')}
          </title>
          <meta
            name="description"
            content={
              metaDescriptionOverride ||
              (isCheckoutEntry
                ? 'Create or sign in to BotSpot, then return to checkout with your plan saved.'
                : 'Sign in to BotSpot with Google, Apple, a passkey, or email.')
            }
          />
          {canonicalHrefOverride && <link rel="canonical" href={canonicalHrefOverride} />}
        </Helmet>
      )}
      <Helmet>
        <meta name="theme-color" content="#080b18" />
      </Helmet>

      <Content
        aria-label={authHeading}
        className="auth-first-sales__container auth-first-sales__container--auth-only"
      >
        <div className="auth-first-auth-only">
          <a aria-label="BotSpot home" className="clarity-auth__brand" href="/">
            BotSpot<span>by LumiWealth</span>
          </a>
          {authContent}
        </div>
      </Content>
    </div>
  );
};

export default AuthFirstSalesPage;
